import React from 'react'
import { X, Check, TrendingUp, Clock, DollarSign } from 'lucide-react'

export default function Transformation() {
  const beforeItems = [
    { icon: Clock, text: '3 hours admin work daily', color: 'text-red-500' },
    { icon: X, text: 'Missed 5-10 follow-ups weekly', color: 'text-red-500' },
    { icon: DollarSign, text: 'Lost ₱15,000/month in forgotten orders', color: 'text-red-500' }
  ]

  const afterItems = [
    { icon: Clock, text: '30 minutes admin work', color: 'text-green-500' },
    { icon: Check, text: 'Zero missed follow-ups (automated)', color: 'text-green-500' },
    { icon: TrendingUp, text: '35% revenue increase', color: 'text-green-500' }
  ]

  return (
    <section className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-100 to-blue-100 dark:from-cyan-900/30 dark:to-blue-900/30 rounded-full mb-4">
            <span className="text-cyan-700 dark:text-cyan-400 font-semibold text-sm">
              ⚡ TRANSFORMATION
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            24 Oras Lang,
            <span className="gradient-text"> Ibang Level Na</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            See the dramatic difference LaundryMate makes to your daily operations
          </p>
        </div>

        {/* Before/After Comparison */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* BEFORE */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 rounded-2xl p-8 border-2 border-red-200 dark:border-red-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                <X className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">BEFORE LaundryMate</h3>
            </div>

            <div className="space-y-4">
              {beforeItems.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                  <div className={`w-6 h-6 mt-0.5 flex-shrink-0 ${item.color}`}>
                    <item.icon className="w-full h-full" />
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-red-100 dark:bg-red-900/30 rounded-lg border border-red-300 dark:border-red-700">
              <p className="text-red-800 dark:text-red-300 font-bold text-center">
                😰 Stressed, overwhelmed, losing money
              </p>
            </div>
          </div>

          {/* AFTER */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-8 border-2 border-green-200 dark:border-green-800 relative overflow-hidden">
            {/* Sparkle effect */}
            <div className="absolute top-4 right-4 text-yellow-400 opacity-50">
              <div className="animate-pulse">✨</div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">AFTER LaundryMate</h3>
            </div>

            <div className="space-y-4">
              {afterItems.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                  <div className={`w-6 h-6 mt-0.5 flex-shrink-0 ${item.color}`}>
                    <item.icon className="w-full h-full" />
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg border border-green-300 dark:border-green-700">
              <p className="text-green-800 dark:text-green-300 font-bold text-center">
                😊 Organized, efficient, growing fast
              </p>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-full shadow-lg">
            <div className="text-left">
              <p className="text-xs opacity-90">Setup Time</p>
              <p className="text-2xl font-bold">Under 1 Hour</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <span className="text-2xl">⚡</span>
            </div>
            <div className="text-left">
              <p className="text-xs opacity-90">See Results</p>
              <p className="text-2xl font-bold">24 Hours</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 dark:text-gray-400 mb-4 text-lg">
            Ready to transform your laundry business?
          </p>
          <a
            href="https://app.laundrymate.shop"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
          >
            Get Started Now
          </a>
        </div>
      </div>
    </section>
  )
}
